import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { LOGO_URL } from "@/api";

const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F7EFE3]/85 border-b transition-all duration-500 ${
        scrolled ? "border-gold/30 shadow-[0_8px_40px_rgba(74,18,26,0.08)]" : "border-maroon/10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="relative block w-9 h-9 sm:w-11 sm:h-11 rounded-full overflow-hidden ring-1 ring-gold/50">
            <img src={LOGO_URL} alt="Vaiyra emblem" className="w-full h-full object-cover" />
          </span>
          <span className="leading-none">
            <span className="block font-brand text-lg sm:text-xl tracking-[0.18em] text-maroon-deep">VAIYRA</span>
            <span className="hidden sm:block text-[9px] tracking-[0.42em] text-gold-dark mt-1">ROOTED IN GRACE</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <Link to="/" data-testid="nav-link-home" className={`text-xs tracking-[0.3em] uppercase transition-colors duration-300 hover:text-gold-dark ${pathname === "/" ? "text-gold-dark" : "text-maroon-deep"}`}>
            Maison
          </Link>
          <Link to="/shop" data-testid="nav-link-shop" className={`text-xs tracking-[0.3em] uppercase transition-colors duration-300 hover:text-gold-dark ${pathname.startsWith("/shop") ? "text-gold-dark" : "text-maroon-deep"}`}>
            Collection
          </Link>
          <Link to="/track" data-testid="nav-link-track" className={`text-xs tracking-[0.3em] uppercase transition-colors duration-300 hover:text-gold-dark ${pathname.startsWith("/track") ? "text-gold-dark" : "text-maroon-deep"}`}>
            Track Order
          </Link>
          <a href="https://instagram.com/vaiyra.in" target="_blank" rel="noopener noreferrer" data-testid="nav-link-instagram" className="text-xs tracking-[0.3em] uppercase text-maroon-deep transition-colors duration-300 hover:text-gold-dark">
            Instagram
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cart-trigger"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 border border-maroon/20 hover:border-gold px-3 sm:px-4 py-2 rounded-full transition-colors duration-300 group"
            aria-label={count > 0 ? `Open cart, ${count} items` : "Open cart"}
          >
            <ShoppingBag className="w-4 h-4 text-maroon-deep group-hover:text-gold-dark transition-colors" />
            <span className="hidden sm:block text-xs tracking-[0.25em] uppercase text-maroon-deep">Cart</span>
            {count > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-maroon text-ivory text-[10px] flex items-center justify-center font-medium">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 -mr-1 text-maroon-deep"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-gold/20 bg-ivory-card px-6 py-6 flex flex-col gap-5">
          <Link to="/" data-testid="nav-mobile-home" className="text-sm tracking-[0.3em] uppercase text-maroon-deep">Maison</Link>
          <Link to="/shop" data-testid="nav-mobile-shop" className="text-sm tracking-[0.3em] uppercase text-maroon-deep">Collection</Link>
          <Link to="/track" data-testid="nav-mobile-track" className="text-sm tracking-[0.3em] uppercase text-maroon-deep">Track Order</Link>
          <a href="https://instagram.com/vaiyra.in" target="_blank" rel="noopener noreferrer" data-testid="nav-mobile-instagram" className="text-sm tracking-[0.3em] uppercase text-maroon-deep">Instagram</a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
